import React, { useState, useEffect, useMemo, useRef } from 'react';
import { highlightMatch } from '../ui/highlight-match';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Checkbox, CircularProgress, IconButton, Tooltip, InputBase } from '@mui/material';
import { ChevronDown, Search, X, Link } from 'lucide-react';
import useApi from '../../hooks/use-api';
import routes from '../../api/routes';
import buildApiRequestConfig from '../../api/url-builder';
import { useUser } from '../../providers/user-provider';
import { useToast } from '../../providers/toaster-provider';
import DateRangeFilter from '../ui/date-range-filter';
import TolerancePopover from '../ui/tolerance-popover';
import { useSettings } from '../../providers/settings-provider';

export interface MatchingTransaction {
    id: string;
    date: string;
    rawDate: Date;
    amount: number;
    description: string;
    selected: boolean;
    bankId?: string | number | null;
    // Set when this candidate is a provision created from a Xero invoice
    // import - lets the caller send the row click to the invoice PDF
    // preview instead of the transaction/provision detail modal.
    invoiceNumber?: string | null;
    // True for a row seeded from alreadyLinkedTransactions below - rendered
    // pre-checked among the normal candidates instead of in a separate
    // section, so unticking it reads naturally as "unlink this" the same way
    // ticking a candidate reads as "link this".
    alreadyLinked?: boolean;
}

interface LinkTransactionPopupProps {
    open: boolean;
    onClose: () => void;
    // transactionsToUnlink: guids of already-linked rows the user unticked -
    // a separate list rather than folding them into selectedTransactions
    // since linking and unlinking are different API calls on the caller's side.
    // shouldCloseTransaction: true when linking provisions to a transaction
    // (!isProvision) and their combined total fully covers it - the caller
    // should also mark the transaction closed (isLinkable false) once the
    // link succeeds, same as the manual OPEN/CLOSED toggle already does.
    onConfirm: (selectedTransactions: string[], transactionsToUnlink: string[], shouldCloseTransaction: boolean) => void;
    // True while the caller's own link/unlink API call is in flight - disables
    // Confirm so a slow request (more candidates selected means more rows the
    // backend has to update) can't be submitted a second time by an impatient
    // click, which was landing as two overlapping link requests against the
    // same rows with no feedback in between telling the user anything was
    // happening.
    isSubmitting?: boolean;
    // Opens that transaction/provision's own detail modal on top of this one -
    // lets the user inspect a candidate (or an already-linked row) in full
    // without losing their place here. invoiceNumber is passed alongside so
    // the caller can send invoice-backed rows to the invoice preview instead
    // (see MatchingTransaction.invoiceNumber).
    onRowClick?: (transactionGuid: string, invoiceNumber?: string | null) => void;
    provisionAmount?: number;
    initialToleranceRange?: { min: number; max: number };
    initialDateRange?: { start: string; end: string };
    maxToleranceAmount?: number;
    isProvision?: boolean;
    excludeTransactionId?: string;
    transactionBankId?: string | number | null;
    // The description of the provision/transaction being matched against -
    // shown under the title so it's still visible while scrolling through
    // candidates below, instead of having to remember it (or close this
    // modal to go check) while comparing descriptions in the list.
    sourceDescription?: string;
    // The date of the provision/transaction being matched against, shown
    // alongside the description for the same reason.
    sourceDate?: string | Date | null;
    // A provision's own `amount` gets reduced (often to 0) once a real
    // transaction links/settles against it (see linkTransactions in
    // transaction-controller.js) - originalAmount preserves the true
    // forecasted value, so it's shown here instead of the (possibly already
    // partially-settled) provisionAmount used for tolerance matching.
    sourceOriginalAmount?: number | null;
    // Transactions already linked to this provision/transaction (a provision
    // can be partially matched already) - rendered as pre-checked rows among
    // the candidates (see MatchingTransaction.alreadyLinked) so the user has
    // full context while picking more, instead of this modal only ever
    // showing brand-new candidates. Unticking one signals an unlink.
    alreadyLinkedTransactions?: { guid: string; date: string | Date; amount: number; description?: string; invoiceNumber?: string | null }[];
}

const LinkTransactionPopup: React.FC<LinkTransactionPopupProps> = ({
    open,
    onClose,
    onConfirm,
    isSubmitting = false,
    onRowClick,
    provisionAmount = 600.00,
    initialToleranceRange = { min: 100, max: 1000 },
    initialDateRange = (() => {
        const today = new Date();
        const oneYearBefore = new Date(today);
        oneYearBefore.setFullYear(today.getFullYear() - 1);
        const fortyFiveDaysAfter = new Date(today);
        fortyFiveDaysAfter.setDate(today.getDate() + 45);
        return {
            start: oneYearBefore.toISOString().split('T')[0],
            end: fortyFiveDaysAfter.toISOString().split('T')[0]
        };
    })(),
    maxToleranceAmount = 10000,
    isProvision = false,
    excludeTransactionId,
    transactionBankId,
    sourceDescription,
    sourceDate,
    sourceOriginalAmount,
    alreadyLinkedTransactions = []
}) => {
    const { user } = useUser();
    const { formatAmount, formatDate } = useSettings();
    const { showError } = useToast();
    const [transactions, setTransactions] = useState<MatchingTransaction[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    // The input itself stays bound to searchTerm directly so every keystroke
    // shows up instantly - the expensive part (re-filtering the candidate
    // list, then re-rendering every row's Tooltip + highlightMatch) uses this
    // deferred copy instead, so React can keep the input responsive and only
    // catch the list up once there's spare time. Without this, typing (or
    // clearing via the X button) felt laggy whenever the candidate list was
    // large - most noticeable right after "highlight to search", which
    // widens the tolerance/date range and can leave a very large unfiltered
    // list for a single keystroke to re-render synchronously.
    const deferredSearchTerm = React.useDeferredValue(searchTerm);
    // 'closest' (the default) matches the original fixed behavior - nearest
    // to the source amount first, regardless of column - only overridden once
    // the user actually clicks a column header. Already-linked rows are
    // never affected - they stay pinned in place, this only reorders the
    // candidate list underneath them.
    const [sortBy, setSortBy] = useState<'closest' | 'date' | 'amount' | 'description'>('closest');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const handleSortClick = (column: 'date' | 'amount' | 'description') => {
        if (sortBy === column) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDirection('asc');
        }
    };
    const [fetch, setFetch] = useApi();
    
    const [toleranceRange, setToleranceRange] = useState<number>(0);
    const [customMin, setCustomMin] = useState<number | null>(null);
    const [customMax, setCustomMax] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(initialDateRange.start),
        end: new Date(initialDateRange.end)
    });
    const [toleranceAnchorEl, setToleranceAnchorEl] = useState<HTMLElement | null>(null);

    const [allTransactions, setAllTransactions] = useState<MatchingTransaction[]>([]);

    // Lives separately from `transactions` (the tolerance/date-filtered
    // candidate list) rather than merged into it, so widening/narrowing the
    // tolerance or date range - which rebuilds `transactions` from scratch -
    // can never wipe out a pending untick-to-unlink decision. Keyed by guid,
    // defaults to checked (true) for any guid not yet toggled.
    const [alreadyLinkedSelection, setAlreadyLinkedSelection] = useState<Record<string, boolean>>({});
    useEffect(() => {
        setAlreadyLinkedSelection(Object.fromEntries(alreadyLinkedTransactions.map(t => [t.guid, true])));
    }, [alreadyLinkedTransactions]);

    const alreadyLinkedGuids = useMemo(() => new Set(alreadyLinkedTransactions.map(t => t.guid)), [alreadyLinkedTransactions]);

    const alreadyLinkedRows: MatchingTransaction[] = useMemo(() => alreadyLinkedTransactions.map(t => ({
        id: t.guid,
        date: new Date(t.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        rawDate: new Date(t.date),
        amount: t.amount,
        description: t.description || '',
        selected: alreadyLinkedSelection[t.guid] ?? true,
        alreadyLinked: true,
        invoiceNumber: t.invoiceNumber,
    })), [alreadyLinkedTransactions, alreadyLinkedSelection]);

    // "Highlight to search": selecting part of the source description below
    // surfaces a small button right next to the selection, so the user can
    // filter the candidate list by just that fragment (e.g. a name) instead
    // of the whole (often too-specific) description.
    const sourceDescriptionRef = useRef<HTMLSpanElement>(null);
    const [selectionSearchButton, setSelectionSearchButton] = useState<{ text: string; top: number; left: number } | null>(null);

    useEffect(() => {
        if (!open) return;
        const handleSelectionChange = () => {
            const selection = document.getSelection();
            const text = selection?.toString().trim();
            const anchorNode = selection?.anchorNode;
            if (!text || !anchorNode || !sourceDescriptionRef.current?.contains(anchorNode)) {
                setSelectionSearchButton(null);
                return;
            }
            const range = selection!.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            setSelectionSearchButton({ text, top: rect.top, left: rect.right });
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, [open]);

    const handleSearchSelectedText = () => {
        if (!selectionSearchButton) return;
        // Tolerance/date range are bypassed automatically while any keyword
        // search is active (see isKeywordFilterActive above) - no need to
        // also mutate them here.
        setSearchTerm(selectionSearchButton.text);
        document.getSelection()?.removeAllRanges();
        setSelectionSearchButton(null);
    };

    useEffect(() => {
        if (open && user?.entityId) {
            const params = [
                { param: 'entityId', value: user.entityId },
            ];
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);
            setFetch({ url, method, body: {
                showReal: isProvision ? true : false,
                showRecurring: isProvision ? false : true,
                showOneTime: isProvision ? false : true,
                viewBy: 'transactions',
                // This popup needs every matching candidate to link against, not
                // a page of them - explicit like the other non-paginated callers
                // of this endpoint, so it isn't at the mercy of the default
                // pageSize once lazy loading is enabled.
                pageSize: 99999,
                // startDate/endDate are deliberately NOT sent here - the
                // backend (getTransactionsByEntityId) only applies date
                // filtering inside its simulationId branch; for a plain
                // request like this one it's a no-op, so the Date Range
                // picker is applied entirely client-side below instead (see
                // filteredByToleranceAndDate). Sending them anyway would just
                // trigger a pointless refetch on every date-range tweak
                // without changing what comes back.
            } });
        }
    }, [
        open,
        user?.entityId,
        setFetch,
        isProvision
    ]);

    useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && data.transactions && !isLoading) {
            const fetchedTransactions = data.transactions?.filter((tx: any) =>
                (!excludeTransactionId || tx.guid !== excludeTransactionId) &&
                // Transactions already linked to another provision/transaction
                // aren't offered at all. hasLinkedTransactions covers both
                // directions - the candidate's own linkedTransactionId, AND the
                // reverse case (some other active transaction's
                // linkedTransactionId points at this one, e.g. a Xero-import
                // auto-match that only ever sets the field on the provision it
                // created - see getTransactionsByEntityId,
                // transaction-controller.js). Rows already linked to THIS
                // provision/transaction come in separately as alreadyLinkedRows,
                // so they still show (ticked, to untick and unlink).
                !tx.hasLinkedTransactions &&
                // Bank filtering logic:
                // If transaction has a bank, it must match the source transaction's bank
                // If either transaction has no bank (null), they can be linked
                (
                    !transactionBankId && !tx.bankId ||  // Both have no bank
                    transactionBankId && !tx.bankId ||    // Source has bank, target has no bank
                    !transactionBankId && tx.bankId ||    // Source has no bank, target has bank
                    transactionBankId === tx.bankId       // Both have same bank
                )
            )?.map((tx: any) => ({
                id: tx.guid,
                date: new Date(tx.date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }),
                rawDate: new Date(tx.date),
                amount: tx.amount,
                description: tx.description,
                selected: false,
                bankId: tx.bankId,
                invoiceNumber: tx.invoiceNumber,
            }));
            setAllTransactions(fetchedTransactions);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch transactions");
        }
    }, [fetch, showError, excludeTransactionId, transactionBankId]);

    // Earliest/latest dates among the fetched candidates (before the date
    // range itself narrows them) - lets the picker's quick-set buttons snap
    // to the actual edges of what's available to match against, same as
    // the Transactions page's own Earliest/Latest Record buttons.
    const earliestCandidateDate = useMemo(() => (
        allTransactions.length ? new Date(Math.min(...allTransactions.map(t => t.rawDate.getTime()))) : null
    ), [allTransactions]);
    const latestCandidateDate = useMemo(() => (
        allTransactions.length ? new Date(Math.max(...allTransactions.map(t => t.rawDate.getTime()))) : null
    ), [allTransactions]);

    // A keyword search means "find it wherever it is" - narrowing by
    // tolerance/date range first would just hide the very match the user is
    // searching for behind whatever those filters happen to be set to.
    // Reactive to the search term itself (not a one-time widen), so it
    // applies to anything typed - not only the "search selected text" button
    // below - and reverts automatically once the search is cleared, instead
    // of leaving tolerance/date stuck wide open.
    const isKeywordFilterActive = deferredSearchTerm.trim().length > 0;

    const filteredByTolerance = useMemo(() => {
        // Date Range is applied entirely client-side (see the fetch effect's
        // comment on why) - combined here with the amount/tolerance filter
        // so both actually narrow the same list together, instead of the
        // date range silently doing nothing while tolerance looked like it
        // was the only filter in effect.
        // Excludes anything already in alreadyLinkedRows too - a reverse-
        // direction link (see the comment on alreadyLinkedTransactions in
        // transaction-detail.tsx) can leave linkedTransactionId null on the
        // transaction's own record, so it would otherwise still pass the
        // fetch's own !linkedTransactionId filter and show up a second time
        // here as an ordinary, unchecked candidate.
        const byDate = allTransactions.filter(transaction =>
            !alreadyLinkedGuids.has(transaction.id) &&
            (isKeywordFilterActive || (transaction.rawDate >= dateRange.start && transaction.rawDate <= dateRange.end))
        );

        let byAmount;
        if (isKeywordFilterActive) {
            byAmount = byDate;
        } else if (customMin !== null && customMax !== null) {
            // If custom min/max are set, use those
            byAmount = byDate.filter(transaction =>
                transaction.amount >= customMin && transaction.amount <= customMax
            );
        } else if (toleranceRange === 0) {
            // If tolerance is 0, filter to exact provision amount matches
            byAmount = byDate.filter(transaction =>
                transaction.amount === provisionAmount
            );
        } else {
            // Otherwise use the tolerance symmetrically around provision amount
            const minAmount = provisionAmount - toleranceRange;
            const maxAmount = provisionAmount + toleranceRange;
            byAmount = byDate.filter(transaction =>
                transaction.amount >= minAmount && transaction.amount <= maxAmount
            );
        }

        // Closest-to-source-amount first, so the most likely match is always
        // at the top regardless of how wide the tolerance/date range is.
        return [...byAmount].sort((a, b) =>
            Math.abs(a.amount - provisionAmount) - Math.abs(b.amount - provisionAmount)
        );
    }, [allTransactions, toleranceRange, customMin, customMax, provisionAmount, dateRange.start, dateRange.end, alreadyLinkedGuids, isKeywordFilterActive]);

    useEffect(() => {
        setTransactions(filteredByTolerance);
    }, [filteredByTolerance]);

    useEffect(() => {
        if (open) {
            setTransactions(prev => prev.map(t => ({ ...t, selected: false })));
            setSearchTerm('');
        } else {
            // Reset tolerance when modal closes
            setToleranceAnchorEl(null);
        }
    }, [open]);

    const handleTransactionToggle = (transactionId: string) => {
        if (alreadyLinkedGuids.has(transactionId)) {
            // Untick = mark for unlink, tick = keep linked - resolved into an
            // actual unlink call only on Confirm (see handleConfirm below),
            // same as a new candidate only actually links on Confirm.
            setAlreadyLinkedSelection(prev => ({ ...prev, [transactionId]: !(prev[transactionId] ?? true) }));
            return;
        }
        setTransactions(prev =>
            prev.map(transaction =>
                transaction.id === transactionId
                    ? { ...transaction, selected: !transaction.selected }
                    : transaction
            )
        );
    };


    const selectedTransactions = transactions.filter(t => t.selected);
    const stillLinkedRows = alreadyLinkedRows.filter(r => r.selected);
    const transactionsToUnlink = alreadyLinkedRows.filter(r => !r.selected);
    const selectedTotalAmount = stillLinkedRows.reduce((sum, t) => sum + t.amount, 0) + selectedTransactions.reduce((sum, t) => sum + t.amount, 0);
    // Only meaningful when linking provisions TO a transaction (!isProvision) -
    // "closing" only ever applies to the transaction side of the relationship.
    const isFullyCovered = !isProvision && selectedTransactions.length > 0 && Math.abs(selectedTotalAmount - provisionAmount) < 0.005;

    // Already-linked rows always show regardless of search/tolerance/date
    // filters - they're an established fact the user needs constant access
    // to (to untick and unlink), not a candidate being searched for - and
    // are pinned first so they're never buried under the candidate list.
    const filteredCandidates = transactions.filter(transaction =>
        transaction.description.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
        transaction.date.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
        transaction.amount.toString().includes(deferredSearchTerm)
    );
    // 'closest' needs no re-sort - transactions is already in that order
    // (filteredByTolerance's own sort, above), and filter() preserves it.
    const sortedCandidates = sortBy === 'closest' ? filteredCandidates : [...filteredCandidates].sort((a, b) => {
        let result = 0;
        if (sortBy === 'date') result = a.rawDate.getTime() - b.rawDate.getTime();
        else if (sortBy === 'amount') result = a.amount - b.amount;
        else if (sortBy === 'description') result = a.description.localeCompare(b.description);
        return sortDirection === 'asc' ? result : -result;
    });
    // Rendering every match is the actual bottleneck when the list is large
    // (e.g. right after "highlight to search" widens tolerance to "All
    // values" and the date range to earliest-latest) - each row mounts a
    // Tooltip plus several highlightMatch() calls, so a wide-open filter can
    // mean rendering hundreds/thousands of rows on every keystroke no matter
    // how that render gets scheduled. Capping what actually mounts fixes the
    // lag directly instead of just reprioritizing when it happens.
    const MAX_VISIBLE_CANDIDATES = 150;
    const visibleCandidates = sortedCandidates.slice(0, MAX_VISIBLE_CANDIDATES);
    const hiddenCandidateCount = sortedCandidates.length - visibleCandidates.length;
    const filteredTransactions = [...alreadyLinkedRows, ...visibleCandidates];

    // Single reset path for every filter this popup owns - confirm and
    // cancel each used to repeat this list by hand, which is exactly how
    // dateRange got left out originally (added later, only wired into the
    // useState initializer, never into either reset). Add a new filter here
    // once and both paths pick it up.
    const resetFilters = () => {
        setTransactions(prev => prev.map(t => ({ ...t, selected: false })));
        setAlreadyLinkedSelection(Object.fromEntries(alreadyLinkedTransactions.map(t => [t.guid, true])));
        setToleranceRange(0);
        setCustomMin(null);
        setCustomMax(null);
        setToleranceAnchorEl(null);
        setDateRange({
            start: new Date(initialDateRange.start),
            end: new Date(initialDateRange.end)
        });
        setSortBy('closest');
        setSortDirection('asc');
    };

    const handleConfirm = () => {
        const newlySelectedIds = selectedTransactions.map(t => t.id);
        const unlinkIds = transactionsToUnlink.map(t => t.id);
        const shouldCloseTransaction = isFullyCovered;
        resetFilters();
        onConfirm(newlySelectedIds, unlinkIds, shouldCloseTransaction);
    };

    const handleCancel = () => {
        resetFilters();
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleCancel} 
            maxWidth="md" 
            fullWidth
            PaperProps={{ 
                sx: { 
                    borderRadius: 3, 
                    width: 700,
                    minHeight: 700
                } 
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, fontWeight: 700, fontSize: 22, pb: sourceDescription ? 0.25 : 1 }}>
                {/* Candidates are the opposite type of the source - a provision
                source (isProvision) searches among real transactions, and a
                real-transaction source searches among provisions (see the
                showReal/showRecurring/showOneTime fetch below) - so the title
                should name whichever one is actually being shown, not always
                say "Transactions". */}
                <Box component="span">Find Matching {isProvision ? 'Transactions' : 'Provisions'}</Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        border: '1px solid #c7c7c7ff',
                        borderRadius: 2,
                        height: 34,
                        px: 1.25,
                        flexShrink: 0,
                        '&:focus-within': { borderColor: 'var(--color-primary)' },
                    }}
                >
                    <Search size={15} color="var(--color-text-muted)" />
                    <InputBase
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        sx={{ fontSize: 14, fontWeight: 400, width: 160 }}
                    />
                    {searchTerm && (
                        <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.25 }}>
                            <X size={13} />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>
            {sourceDescription && (
                <Typography sx={{ fontSize: 14, color: 'var(--color-text-muted)', px: 3 }}>
                    Matching against: <Typography ref={sourceDescriptionRef} component="span" sx={{ fontSize: 14, fontWeight: 600, color: 'inherit' }}>{sourceDescription}</Typography>
                </Typography>
            )}
            {sourceDate && (
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', px: 3, pb: isProvision && sourceOriginalAmount != null ? 0 : 1 }}>
                    Date: <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>{formatDate(new Date(sourceDate))}</Typography>
                </Typography>
            )}
            {isProvision && sourceOriginalAmount != null && (
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', px: 3, pb: 1 }}>
                    Original Amount: <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>{formatAmount?.(sourceOriginalAmount)}</Typography>
                </Typography>
            )}
            {selectionSearchButton && (
                <Tooltip title={`Filter list by "${selectionSearchButton.text}"`}>
                    <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSearchSelectedText}
                        sx={{
                            position: 'fixed',
                            top: selectionSearchButton.top - 34,
                            left: selectionSearchButton.left - 16,
                            zIndex: 1400,
                            background: 'var(--color-primary)',
                            color: '#fff',
                            boxShadow: 2,
                            '&:hover': { background: 'var(--color-primary-hover)' },
                        }}
                    >
                        <Search size={16} />
                    </IconButton>
                </Tooltip>
            )}
            <DialogContent sx={{ pt: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Tooltip title={isKeywordFilterActive ? 'Disabled while searching by keyword - clear the search to use tolerance again' : ''}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ opacity: isKeywordFilterActive ? 0.4 : 1 }}>
                            <Typography sx={{ fontSize: 14 }}>
                                Tolerance Range:
                            </Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                    border: '1px solid #c7c7c7ff',
                                    borderRadius: 2,
                                    height: 30,
                                    padding: '0 10px',
                                    cursor: isKeywordFilterActive ? 'default' : 'pointer',
                                    pointerEvents: isKeywordFilterActive ? 'none' : 'auto',
                                    '&:hover': { borderColor: '#212121ff' }
                                }}
                                onClick={(e) => setToleranceAnchorEl(e.currentTarget)}
                            >
                            <Typography sx={{ fontSize: 14, color: isKeywordFilterActive || (customMin !== null && customMax !== null) ? 'var(--color-primary-hover)' : toleranceRange === 0 ? '#a2a2a2ff' : 'var(--color-primary-hover)'}}>
                                {/* A keyword search already bypasses amount filtering entirely
                                (see filteredByTolerance above) - same as the explicit "All values"
                                state below, just reflecting it here too instead of showing a
                                stale tolerance value that isn't actually being applied. */}
                                {isKeywordFilterActive || (customMin === -Infinity && customMax === Infinity)
                                    ? 'All values'
                                    : customMin !== null && customMax !== null
                                        ? `${formatAmount?.(customMin)} - ${formatAmount?.(customMax)}`
                                        : toleranceRange === 0 ? 'Set tolerance' : `± ${toleranceRange}`}
                            </Typography>
                                <ChevronDown size={15} color='#a2a2a2ff' />
                            </Box>
                        </Box>
                    </Tooltip>
                    <Tooltip title={isKeywordFilterActive ? 'Disabled while searching by keyword - clear the search to use date range again' : ''}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ opacity: isKeywordFilterActive ? 0.4 : 1, pointerEvents: isKeywordFilterActive ? 'none' : 'auto' }}>
                            <Typography sx={{ fontSize: 14 }}>Date Range:</Typography>
                            <DateRangeFilter
                                start={dateRange.start}
                                end={dateRange.end}
                                onChange={(start, end) => setDateRange({ start, end })}
                                sx={{ height: 15, fontSize: 12 }}
                                earliestDate={earliestCandidateDate}
                                latestDate={latestCandidateDate}
                                earliestLabel={`Earliest ${isProvision ? 'Transaction' : 'Provision'}`}
                                latestLabel={`Latest ${isProvision ? 'Transaction' : 'Provision'}`}
                            />
                        </Box>
                    </Tooltip>
                </Box>
                <TolerancePopover
                    open={Boolean(toleranceAnchorEl)}
                    anchorEl={toleranceAnchorEl}
                    onClose={() => setToleranceAnchorEl(null)}
                    toleranceValue={toleranceRange}
                    onToleranceChange={setToleranceRange}
                    onCustomRangeChange={(min, max) => {
                        // Clear custom range if NaN (from slider usage)
                        if (isNaN(min) && isNaN(max)) {
                            setCustomMin(null);
                            setCustomMax(null);
                        } else {
                            setCustomMin(min);
                            setCustomMax(max);
                        }
                    }}
                    minToleranceAmount={0}
                    maxToleranceAmount={maxToleranceAmount}
                    provisionAmount={provisionAmount}
                    customMin={customMin}
                    customMax={customMax}
                />
                <Box
                    display="flex"
                    alignItems="center"
                    py={0.75}
                    px={2}
                    gap={1}
                    sx={{
                        background: 'var(--color-container-background)',
                        borderRadius: '8px 8px 0 0',
                        fontWeight: 600,
                        fontSize: 14
                    }}
                >
                    <Box width={25}></Box>
                    {([
                        { key: 'date' as const, label: 'Date', width: 110 },
                        { key: 'amount' as const, label: 'Amount', width: 80 },
                        { key: 'description' as const, label: 'Description', width: undefined },
                    ]).map(col => (
                        <Box
                            key={col.key}
                            width={col.width}
                            flex={col.width ? undefined : 1}
                            onClick={() => handleSortClick(col.key)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'var(--color-primary)' } }}
                        >
                            {col.label}
                            <Typography component="span" sx={{ fontSize: 10, opacity: sortBy === col.key ? 1 : 0.3, color: sortBy === col.key ? 'var(--color-primary)' : 'inherit' }}>
                                {sortBy === col.key ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Box 
                    sx={{ 
                        borderRadius: '0 0 8px 8px',
                        maxHeight: 350,
                        overflowY: 'auto'
                    }}
                >
                    {fetch.isLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ ml: 2 }}>Loading transactions...</Typography>
                        </Box>
                    ) : filteredTransactions.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <Typography variant="body2" color="text.secondary">
                                No transactions found matching the criteria
                            </Typography>
                        </Box>
                    ) : (
                        filteredTransactions.map((transaction, index) => (
                            <Box
                                key={transaction.id}
                                display="flex"
                                alignItems="flex-start"
                                minHeight={20}
                                py={0.25}
                                px={2}
                                gap={1}
                                sx={{
                                    lineHeight: '20px',
                                    borderBottom: index < filteredTransactions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    // Already-linked rows get their own tint so unticking one
                                    // (marking it for unlink) reads as a distinct, slightly
                                    // alarming change rather than looking like an ordinary
                                    // unchecked candidate.
                                    background: transaction.alreadyLinked
                                        ? (transaction.selected ? '#e8f5f0' : '#fdeaea')
                                        : (transaction.selected ? '#e3f2fd' : 'white'),
                                    '&:hover': { background: transaction.alreadyLinked
                                        ? (transaction.selected ? '#e8f5f0' : '#fdeaea')
                                        : (transaction.selected ? '#e3f2fd' : 'var(--color-container-background)') },
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    fontWeight: transaction.selected ? 600 : 400,
                                }}
                                onClick={() => onRowClick?.(transaction.id, transaction.invoiceNumber)}
                            >
                                <Box width={25} onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={transaction.selected}
                                        size='small'
                                        onChange={() => handleTransactionToggle(transaction.id)}
                                        sx={{ p: 0 }}
                                    />
                                </Box>
                                <Box width={110} fontSize={14} flexShrink={0}>
                                    {highlightMatch(formatDate(new Date(transaction.date)), deferredSearchTerm)}
                                </Box>
                                <Box width={80} fontSize={14} fontWeight={500} textAlign="right" flexShrink={0}>
                                    {highlightMatch(formatAmount?.(transaction.amount) ?? '', deferredSearchTerm)}
                                </Box>
                                <Box flex={1} fontSize={14} display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {transaction.alreadyLinked && (
                                        <Tooltip title="Already linked - untick to unlink" arrow>
                                            <Link size={13} color="var(--color-text-muted)" style={{ flexShrink: 0 }} />
                                        </Tooltip>
                                    )}
                                    {/* A single span, not the highlighted fragments spread
                                    directly into the flex container above - each text/<mark>
                                    piece highlightMatch returns would otherwise become its own
                                    flex item (gap included) instead of flowing as one run of
                                    inline text, which is what was breaking the wrapping. */}
                                    <span>{highlightMatch(transaction.description, deferredSearchTerm)}</span>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
                {hiddenCandidateCount > 0 && (
                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', pt: 1 }}>
                        Showing {MAX_VISIBLE_CANDIDATES} of {filteredCandidates.length} matches - narrow your search, tolerance, or date range to see the rest.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ display: 'flex', gap: 2, p: 3 }}>
                <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center"
                    flexDirection="column"
                    flex={1.25}
                    px={2}
                >
                    <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" gap={2}>
                        <Typography sx={{ color: isProvision ? 'var(--color-provision-text)' : 'var(--color-primary)', fontWeight: 600 }}>
                            {isProvision ? 'Provision ' : 'Transaction '}Amount:
                        </Typography>
                        <Typography sx={{ color: isProvision ? 'var(--color-provision-text)' : 'var(--color-primary)', fontWeight: 600, width: 120, textAlign: 'right', textWrap: 'nowrap' }}>
                            {formatAmount?.(provisionAmount)}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" gap={2}>
                        <Typography sx={{ fontWeight: 600 }}>
                            Selected Total Amount:
                        </Typography>
                        <Typography sx={{ fontWeight: 600, width: 120, textAlign: 'right', textWrap: 'nowrap' }}>
                            {formatAmount?.(selectedTotalAmount)}
                        </Typography>
                    </Box>
                </Box>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 1.5
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={isSubmitting || (selectedTransactions.length === 0 && transactionsToUnlink.length === 0)}
                        startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{
                            background: 'var(--color-primary)',
                            borderRadius: 2,
                            textTransform: 'none',
                            color: '#fff',
                            px: 1.5,
                            '&:hover': { background: '#23615bff' }
                        }}
                    >
                        {isSubmitting
                            ? 'Linking…'
                            : selectedTransactions.length > 0 && transactionsToUnlink.length > 0
                            ? 'Link & Unlink Transactions'
                            : selectedTransactions.length > 0
                                ? (isFullyCovered ? 'Link and Close Transaction' : 'Link Transactions')
                                : transactionsToUnlink.length > 0
                                    ? `Unlink Transaction${transactionsToUnlink.length > 1 ? 's' : ''}`
                                    : 'Link Transactions'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default LinkTransactionPopup;
